import { create } from 'zustand';
import type { Message, UiFlowPayload, ConnectionState, PixPayload, Cotacao, OfertaAlt, OutgoingPayload } from '../types/agent';
import { TOOL_PHRASES, TOOL_PHRASE_FALLBACK } from '../types/agent';

// Flow types that render inline alongside LLM text (non-terminal).
// Their ui_payload and quick_replies are buffered until markDone so they
// appear AFTER the confirmation text, not before it.
const DEFERRED_FLOW_TYPES = new Set(['suggestions', 'instagram_news']);

export interface ChatStore {
  /** Sessão atual do gateway (muda a cada "nova conversa"). */
  sessionId: string;
  messages: Message[];
  connectionState: ConnectionState;
  pendingUserMessageId: string | null;
  pendingToolCallId: string | null;
  isAutoScrollEnabled: boolean;
  /** True after a tool result arrives — next text chunk goes into a NEW bubble. */
  splitNextChunk: boolean;
  /** Buffered ui_payload waiting to be flushed after text finishes. */
  pendingUiPayload: UiFlowPayload | null;
  /** Buffered quick-reply labels waiting to be flushed after text finishes. */
  pendingQuickReplies: string[] | null;

  sendMessage: (text: string) => void;
  /** Zera o chat e começa uma sessão nova (botão "nova conversa" da Home). */
  novaConversa: () => void;
  /** Restaura uma conversa arquivada (Home -> tocar numa conversa antiga). */
  carregarConversa: (sessionId: string, messages: Message[]) => void;
  /** Envia um payload multimodal genérico (texto/imagem/áudio) pelo mesmo caminho de stream. */
  sendPayload: (payload: OutgoingPayload) => void;
  appendTextChunk: (chunk: string) => void;
  setDelivered: () => void;
  setToolStatus: (name: string) => void;
  consumeToolResult: () => void;
  attachUiPayload: (payload: UiFlowPayload) => void;
  attachPriceWarning: (warning: string) => void;
  attachCotacao: (cotacao: Cotacao) => void;
  attachOfertaAlt: (oferta: OfertaAlt) => void;
  attachPixPayload: (payload: PixPayload) => void;
  attachQuickReplies: (labels: string[]) => void;
  dismissFlow: (messageId: string) => void;
  markDone: () => void;
  setConnectionState: (state: ConnectionState) => void;
  setAutoScroll: (enabled: boolean) => void;
}

export const useChatStore = create<ChatStore>()((set, get) => ({
  sessionId: crypto.randomUUID(),
  messages: [],
  connectionState: 'idle',
  pendingUserMessageId: null,
  pendingToolCallId: null,
  isAutoScrollEnabled: true,
  splitNextChunk: false,
  pendingUiPayload: null,
  pendingQuickReplies: null,

  sendMessage: (text) => {
    get().sendPayload({ tipo: 'texto', conteudo: text });
  },

  novaConversa: () => {
    set({
      sessionId: crypto.randomUUID(),
      messages: [],
      connectionState: 'idle',
      pendingUserMessageId: null,
      pendingToolCallId: null,
      splitNextChunk: false,
      pendingUiPayload: null,
      pendingQuickReplies: null,
    });
  },

  carregarConversa: (sessionId, messages) => {
    set({
      sessionId,
      messages,
      connectionState: 'idle',
      pendingUserMessageId: null,
      pendingToolCallId: null,
      splitNextChunk: false,
      pendingUiPayload: null,
      pendingQuickReplies: null,
    });
  },

  sendPayload: (payload) => {
    const id = crypto.randomUUID();
    // For text, the bubble shows the typed message. For multimodal payloads the
    // bubble shows a preview (image) or an audio chip instead of raw base64.
    const isText = payload.tipo === 'texto';
    const userMsg: Message = {
      id,
      role: 'user',
      text: isText ? payload.conteudo : '',
      timestamp: Date.now(),
      deliveryState: 'sending',
      outgoing: payload,
      ...(payload.tipo === 'imagem' ? { imageDataUrl: payload.conteudo } : {}),
      ...(payload.tipo === 'audio' ? { isAudio: true } : {}),
      ...(payload.tipo === 'pdf' ? { pdfName: payload.nome ?? 'documento.pdf' } : {}),
    };
    set((s) => ({
      // Clear quick replies from every message so the pill strip disappears
      // the moment the user sends (or taps a quick reply button).
      messages: [
        ...s.messages.map((m) => (m.quickReplies ? { ...m, quickReplies: undefined } : m)),
        userMsg,
      ],
      pendingUserMessageId: id,
      connectionState: 'streaming',
      splitNextChunk: false,
      // Flush any buffers left over from an interrupted previous turn.
      pendingUiPayload: null,
      pendingQuickReplies: null,
    }));
  },

  appendTextChunk: (chunk) => {
    set((s) => {
      const last = s.messages.at(-1);
      // Append to existing assistant bubble unless a tool just completed (splitNextChunk).
      if (last?.role === 'assistant' && !s.splitNextChunk) {
        return {
          messages: s.messages.map((m, i) =>
            i === s.messages.length - 1 ? { ...m, text: m.text + chunk } : m
          ),
        };
      }
      return {
        splitNextChunk: false,
        messages: [
          ...s.messages,
          {
            id: crypto.randomUUID(),
            role: 'assistant',
            text: chunk,
            timestamp: Date.now(),
            deliveryState: 'read',
          },
        ],
      };
    });
  },

  setDelivered: () => {
    const id = get().pendingUserMessageId;
    if (!id) return;
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === id ? { ...m, deliveryState: 'delivered' } : m
      ),
    }));
  },

  setToolStatus: (name) => {
    const phrase = TOOL_PHRASES[name] ?? TOOL_PHRASE_FALLBACK;
    const id = crypto.randomUUID();
    set((s) => ({
      messages: [
        ...s.messages,
        { id, role: 'tool_status', text: phrase, timestamp: Date.now(), deliveryState: 'read' },
      ],
      pendingToolCallId: id,
    }));
  },

  consumeToolResult: () => {
    const id = get().pendingToolCallId;
    if (!id) return;
    set((s) => ({
      messages: s.messages.filter((m) => m.id !== id),
      pendingToolCallId: null,
      // Next text chunk from LLM (post-tool resumption) goes into a fresh bubble.
      splitNextChunk: true,
    }));
  },

  attachUiPayload: (payload) => {
    // Inline flows (suggestions, instagram_news) arrive before the LLM's
    // confirmation text. Buffer them so they render AFTER the text, not before.
    if (DEFERRED_FLOW_TYPES.has(payload.flow_type)) {
      set({ pendingUiPayload: payload });
      return;
    }
    // Terminal-tool flows (menu, address, event_visit) attach immediately.
    set((s) => {
      const last = s.messages.at(-1);
      if (!last || last.role !== 'assistant') {
        return {
          messages: [
            ...s.messages,
            {
              id: crypto.randomUUID(),
              role: 'assistant',
              text: '',
              timestamp: Date.now(),
              deliveryState: 'read',
              uiPayload: payload,
            },
          ],
        };
      }
      return {
        messages: s.messages.map((m, i) =>
          i === s.messages.length - 1 ? { ...m, uiPayload: payload } : m
        ),
      };
    });
  },

  attachPriceWarning: (warning) => {
    set((s) => {
      const last = s.messages.at(-1);
      if (!last || last.role !== 'assistant') return s;
      return {
        messages: s.messages.map((m, i) =>
          i === s.messages.length - 1 ? { ...m, priceWarning: warning } : m
        ),
      };
    });
  },

  attachCotacao: (cotacao) => {
    // O backend emite o evento `cotacao` ANTES do texto do LLM. Anexa-o à última
    // bolha do assistente; se ainda não existir nenhuma, cria uma placeholder para
    // o texto que vem a seguir ser anexado nela (appendTextChunk reaproveita a
    // última bolha do assistente).
    //
    // MÚLTIPLAS FÓRMULAS: o backend emite um evento `cotacao` POR fórmula, todos na
    // MESMA bolha (mesmo turno). Sem acumular, cada novo evento SOBRESCREVIA o anterior
    // — só a última fórmula sobrevivia no card/checkout (achado real 2026-07-06: receita
    // de 2 fórmulas virava um card mostrando só o preço da 2ª). Acumula em `cotacoes`
    // (mantendo `cotacao` = a última, pra qualquer leitor que só conheça o campo singular).
    set((s) => {
      const last = s.messages.at(-1);
      if (!last || last.role !== 'assistant') {
        return {
          messages: [
            ...s.messages,
            {
              id: crypto.randomUUID(),
              role: 'assistant',
              text: '',
              timestamp: Date.now(),
              deliveryState: 'read',
              cotacao,
              cotacoes: [cotacao],
            },
          ],
        };
      }
      return {
        messages: s.messages.map((m, i) =>
          i === s.messages.length - 1
            ? { ...m, cotacao, cotacoes: [...(m.cotacoes ?? []), cotacao] }
            : m
        ),
      };
    });
  },

  attachOfertaAlt: (oferta) => {
    // Chega logo APÓS `cotacao` (que já criou/anexou a bolha assistant) e ANTES do
    // texto. Anexa à mesma última bolha do assistente; cria placeholder se preciso —
    // espelha `attachCotacao` para a faixa aparecer sob o CotacaoCard.
    set((s) => {
      const last = s.messages.at(-1);
      if (!last || last.role !== 'assistant') {
        return {
          messages: [
            ...s.messages,
            {
              id: crypto.randomUUID(),
              role: 'assistant',
              text: '',
              timestamp: Date.now(),
              deliveryState: 'read',
              ofertaAlt: oferta,
            },
          ],
        };
      }
      return {
        messages: s.messages.map((m, i) =>
          i === s.messages.length - 1 ? { ...m, ofertaAlt: oferta } : m
        ),
      };
    });
  },

  attachPixPayload: (payload) =>
    set((s) => ({
      messages: [
        ...s.messages,
        {
          id: crypto.randomUUID(),
          role: 'assistant',
          text: '',
          timestamp: Date.now(),
          deliveryState: 'read',
          pixPayload: payload,
        },
      ],
    })),

  attachQuickReplies: (labels) => {
    // Buffer quick replies — they arrive before the LLM's confirmation text streams,
    // so we defer attachment until markDone flushes them to the last assistant bubble.
    set({ pendingQuickReplies: labels });
  },

  dismissFlow: (messageId) => {
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === messageId ? { ...m, uiPayload: undefined } : m
      ),
    }));
  },

  markDone: () => {
    const id = get().pendingUserMessageId;
    if (!id) return;
    const { pendingUiPayload, pendingQuickReplies } = get();
    set((s) => {
      // Mark the user message as read.
      let messages: Message[] = s.messages.map((m) =>
        m.id === id ? { ...m, deliveryState: 'read' as const } : m
      );

      // Flush any buffered ui_payload / quick_replies onto the last assistant bubble.
      // These were deferred so they render AFTER the confirmation text, not before it.
      if (pendingUiPayload || pendingQuickReplies) {
        const lastAssistantIdx = messages.reduce<number>(
          (found, m, i) => (m.role === 'assistant' ? i : found),
          -1
        );
        if (lastAssistantIdx !== -1) {
          messages = messages.map((m, i) =>
            i === lastAssistantIdx
              ? {
                  ...m,
                  ...(pendingUiPayload ? { uiPayload: pendingUiPayload } : {}),
                  ...(pendingQuickReplies
                    ? { quickReplies: pendingQuickReplies.map((l) => ({ label: l, value: l })) }
                    : {}),
                }
              : m
          );
        }
      }

      return {
        messages,
        pendingUserMessageId: null,
        connectionState: 'connected',
        pendingUiPayload: null,
        pendingQuickReplies: null,
      };
    });
  },

  setConnectionState: (state) => set({ connectionState: state }),
  setAutoScroll: (enabled) => set({ isAutoScrollEnabled: enabled }),
}));
